import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { OrderTimeline } from "@/components/OrderTimeline";
import { TierBadge } from "@/components/TierBadge";
import { pickTier } from "@/lib/loyalty";
import { Award, Gift, Package } from "lucide-react";

export default function MyOrders() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    api.get("/orders/mine")
      .then(({ data }) => setOrders(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/loyalty-config").then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
  }, [user]);

  const userTier = user ? pickTier(tiers, user.points_lifetime ?? user.points ?? 0) : null;

  if (user === undefined) return <div className="p-20 text-center text-[#9CA3AF]">…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 page-enter" data-testid="my-orders-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-display text-4xl">{t("myOrders")}</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {userTier && <TierBadge tier={userTier} />}
          <div
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37]"
            data-testid="my-points-balance"
          >
            <Award size={18} />
            <span className="text-sm">{t("myPoints")}:</span>
            <span className="font-display text-lg num-ltr">{user.points || 0}</span>
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-20 text-[#9CA3AF]">…</div>}

      {!loading && orders.length === 0 && (
        <div className="text-center py-20 text-[#9CA3AF]" data-testid="no-orders">
          <Package size={40} className="mx-auto mb-4 opacity-40" />
          {t("noOrdersYet")}
        </div>
      )}

      <div className="space-y-5">
        {orders.map((o) => (
          <div
            key={o.id}
            className="bg-[#121214] border border-[#27272A] rounded-2xl p-6"
            data-testid={`my-order-${o.id}`}
          >
            <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
              <div>
                <div className="text-xs text-[#9CA3AF]">
                  #<span className="num-ltr">{o.id.slice(0, 8)}</span> · {new Date(o.created_at).toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mt-1">
                  {o.payment_method} · {o.payment_status}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#D4AF37] font-display text-xl num-ltr">
                  {o.total.toFixed(2)} {t("currency")}
                </div>
              </div>
            </div>

            <div className="mb-5 py-3 px-1 bg-[#0A0A0B]/40 rounded-lg border border-[#27272A]">
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mb-3 px-2">
                {t("orderTracking")}
              </div>
              <OrderTimeline status={o.order_status} />
            </div>

            <div className="text-xs text-[#9CA3AF] flex flex-wrap gap-2">
              {o.items.map((i) => (
                <span key={i.product_id} className="px-2 py-1 border border-[#27272A] rounded">
                  {lang === "ar" ? i.name_ar : i.name_en} × <span className="num-ltr">{i.quantity}</span>
                </span>
              ))}
            </div>

            {((o.points_earned || 0) > 0 || (o.points_redeemed || 0) > 0) && (
              <div className="flex flex-wrap gap-2 mt-3 text-[10px]">
                {(o.points_earned || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] num-ltr">
                    <Award size={10} /> {t("orderPointsEarned")}: +{o.points_earned}
                  </span>
                )}
                {(o.points_redeemed || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#10B981]/30 text-[#10B981] num-ltr">
                    <Gift size={10} /> −{o.points_redeemed} {t("points")} ({(o.points_discount || 0).toFixed(2)} {t("currency")})
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
